import 'rc-time-picker/assets/index.css';

import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { parseISO, formatISO } from 'date-fns';
import { useAtom } from 'jotai';
import ReactTimePicker from 'rc-time-picker';
import moment from 'moment';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

import { ButtonV2, DatePicker, TextInput, Title } from '@restworld/ui-ds';
import { useResponsiveBreakPoints } from '@restworld/utils-common';

import useUpdateJobApplication from '../../../hooks/use-update-job-application';
import { candidateModalVariantAtom, isCandidateModalSuccessScreenAtom } from '../../../employer-job-listing.atoms';
import { useEmployerJobApplicationQueryKey } from '../../../hooks/use-employer-job-applications';

export enum InterviewScheduleSteps {
  ACTION_CONFIRMATION,
  SELECT_DATE_TIME,
  VIEW_SUCCESS_MESSAGE,
}

function CandidateInterviewDateTime({
  firstName,
  jobPositionId,
  workerId,
  setStep,
  step,
}: {
  firstName: string;
  workerId: string;
  jobPositionId: string;
  setStep: Dispatch<SetStateAction<InterviewScheduleSteps>>;
  step: InterviewScheduleSteps;
}) {
  const { t: translateCommon } = useTranslation('common');
  const { t } = useTranslation('job-positions-page');

  const { control, handleSubmit, watch } = useForm<InterviewFormValues>();
  const { mutate, isSuccess, isPending } = useUpdateJobApplication();
  const queryClient = useQueryClient();
  const [, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);

  const canSubmit = !!watch('date') && !!watch('time');

  const onSubmit: SubmitHandler<InterviewFormValues> = useCallback(
    (values) => {
      const interviewDate = values.date && values.time ? toISODateTime(values.date, values.time) : null;
      mutate({
        params: {
          interview_date: interviewDate,
          status_key: 'worker_jp_interview_scheduled',
        },
        workerId,
        jobPositionId,
      });
    },
    [jobPositionId, mutate, workerId]
  );

  const handleCancel = useCallback(() => {
    setStep((prev) => (prev > 0 ? prev - 1 : prev));
    setCandidateModalVariant('default');
  }, [setCandidateModalVariant, setStep]);

  const employerJobApplicationQueryKey = useEmployerJobApplicationQueryKey();

  useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries({ queryKey: employerJobApplicationQueryKey });
    setIsCandidateModalSuccessScreen(true);
    setStep(InterviewScheduleSteps.VIEW_SUCCESS_MESSAGE);
  }, [employerJobApplicationQueryKey, isSuccess, queryClient, setIsCandidateModalSuccessScreen, setStep]);

  const { isDesktop } = useResponsiveBreakPoints();

  return (
    <StyledForm>
      <ContentAndInputsWrapper>
        <CalendarDaysIcon tw="h-6 w-6 text-neutral-700 mx-auto mb-5" />
        <Title fontSize="large" textColor="neutral-700" fontWeight="semibold" twStyle={tw`w-full text-base lg:text-lg`}>
          {`${t('when_did_you_schedule_the_interview_with')} ${firstName}?`}
        </Title>
        <ContentWrapper>
          {`${t('tell_us_date_and_time_of_the_interview')}.`}
          <br />
          <strong>*({t('this_can_reduce_no_shows_up_to_82_percent')})</strong>
        </ContentWrapper>
        <InputsWrapper>
          <InputWrapper>
            <StyledLabel htmlFor="date">{t('select_the_date_of_the_interview')}</StyledLabel>
            <Controller
              name="date"
              control={control}
              render={({ field }) => <DatePicker borderColor="neutral-100" {...field} />}
            />
          </InputWrapper>
          <InputWrapper>
            <StyledLabel htmlFor="time">{t('select_the_time_of_the_interview')}</StyledLabel>
            <Controller
              name="time"
              control={control}
              render={({ field }) =>
                !isDesktop ? (
                  <TextInput {...field} id="time" type="time" placeholder="HH:MM" twStyle={tw`w-full`} />
                ) : (
                  <ReactTimePickerStyled
                    clearIcon={null}
                    placeholder="HH:mm"
                    clearText={null}
                    showSecond={false}
                    format="HH:mm"
                    {...field}
                    onChange={(value) => {
                      field.onChange(value ? value.format('HH:mm') : '');
                    }}
                    value={field.value ? moment(field.value, 'HH:mm') : null}
                    id="time"
                    className="time-picker"
                    defaultValue={moment(new Date().toLocaleTimeString().slice(0, 5), 'HH:mm')}
                    placement="topLeft"
                    inputReadOnly
                    minuteStep={15}
                  />
                )
              }
            />
          </InputWrapper>
        </InputsWrapper>
      </ContentAndInputsWrapper>
      <ButtonsWrapper>
        <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
          {translateCommon('cancel')}
        </ButtonV2>
        <EndActionsWrapper>
          {!canSubmit && (
            <ButtonV2
              variant="secondary-neutral"
              loading={isPending}
              twStyle={tw`bg-transparent`}
              onClick={handleSubmit(onSubmit)}
            >
              {t('skip_step')}
            </ButtonV2>
          )}
          <ButtonV2 loading={isPending} disabled={!canSubmit} onClick={handleSubmit(onSubmit)}>
            {t('set_reminder')}
          </ButtonV2>
        </EndActionsWrapper>
      </ButtonsWrapper>
    </StyledForm>
  );
}
export default CandidateInterviewDateTime;

const StyledForm = styled.form`
  ${tw`h-full flex flex-col justify-between lg:(p-10)`}
`;
const ContentWrapper = styled.p`
  ${tw`text-neutral-200 mt-5 mb-12 font-medium`}
`;
const ContentAndInputsWrapper = styled.div`
  ${tw``}
`;
const InputsWrapper = styled.div`
  ${tw`max-w-[320px] mx-auto flex flex-col gap-10`}
`;
const InputWrapper = styled.div<{ isHidden?: boolean }>`
  ${tw`flex flex-col gap-2`}
  ${({ isHidden }) => isHidden && tw`hidden`}
`;
const StyledLabel = styled.label`
  ${tw`text-start font-medium text-neutral-700 text-base`}
`;
const ButtonsWrapper = styled.div`
  ${tw`mt-8 flex justify-between items-center p-4 gap-4 border-neutral-40 lg:(px-20 border-t absolute bottom-0 -left-10 -right-10)`}
`;
const EndActionsWrapper = styled.div`
  ${tw`flex items-center gap-2 lg:(gap-5)`}
`;

interface InterviewFormValues {
  date?: string;
  time?: string;
}

function toISODateTime(date: string, time: string): string {
  const dateTimeString = `${date}T${time}:00`;
  const parsedDate = parseISO(dateTimeString);
  return formatISO(parsedDate);
}

const ReactTimePickerStyled = styled(ReactTimePicker)`
  input {
    ${tw`h-12 pl-4 pr-2 py-4 rounded-lg border border-neutral-100 justify-start items-center gap-2 inline-flex  focus:(border-neutral-100 ring-neutral-100)`}
    ${tw`grow shrink basis-0 text-neutral-700 text-base font-normal leading-none`}
  }
  .rc-time-picker-clear {
    ${tw`hidden`}
    ${tw`top-[10px]`}
  }
`;
